import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import { Checkbox } from '../../../components/ui/Checkbox';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useTranslation } from 'react-i18next';

const PaymentForm = ({ onPaymentChange, orderTotal = 0 }) => {
  const { t } = useTranslation();
  
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [cardData, setCardData] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardholderName: '',
    saveCard: false
  });
  const [errors, setErrors] = useState({});
  const [isProcessing, setIsProcessing] = useState(false);

  const paymentMethods = [
    {
      id: 'cod',
      label: t('cashOnDelivery'),
      icon: 'Banknote',
      description: t('payWhenReceive')
    }
  ];

  useEffect(() => {
    if (onPaymentChange) {
      onPaymentChange({
        method: paymentMethod,
        data: paymentMethod === 'card' ? cardData : {},
        isValid: paymentMethod === 'cod' || (paymentMethod === 'card' && isCardValid())
      });
    }
  }, [paymentMethod, cardData, onPaymentChange]);

  const isCardValid = () => {
    return cardData.cardNumber.length >= 16 &&
           cardData.expiryDate.length >= 5 &&
           cardData.cvv.length >= 3 &&
           cardData.cardholderName.trim().length >= 2 &&
           Object.keys(errors).length === 0;
  };

  const validateCardField = (name, value) => {
    const newErrors = { ...errors };

    switch (name) {
      case 'cardNumber':
        const cleanNumber = value.replace(/\s/g, '');
        if (!cleanNumber) {
          newErrors.cardNumber = t('cardNumberRequired');
        } else if (cleanNumber.length < 16) {
          newErrors.cardNumber = t('cardNumberLength');
        } else if (!/^\d+$/.test(cleanNumber)) {
          newErrors.cardNumber = t('cardNumberDigits');
        } else {
          delete newErrors.cardNumber;
        }
        break;

      case 'expiryDate':
        if (!value) {
          newErrors.expiryDate = t('expiryDateRequired');
        } else if (!/^\d{2}\/\d{2}$/.test(value)) {
          newErrors.expiryDate = t('expiryDateFormat');
        } else {
          const [month, year] = value.split('/');
          const currentDate = new Date();
          const currentYear = currentDate.getFullYear() % 100;
          const currentMonth = currentDate.getMonth() + 1;
          
          if (parseInt(month) < 1 || parseInt(month) > 12) {
            newErrors.expiryDate = t('invalidMonth');
          } else if (parseInt(year) < currentYear || (parseInt(year) === currentYear && parseInt(month) < currentMonth)) {
            newErrors.expiryDate = t('cardExpired');
          } else {
            delete newErrors.expiryDate;
          }
        }
        break;

      case 'cvv':
        if (!value) {
          newErrors.cvv = t('cvvRequired');
        } else if (!/^\d{3,4}$/.test(value)) {
          newErrors.cvv = t('cvvLength');
        } else {
          delete newErrors.cvv;
        }
        break;

      case 'cardholderName':
        if (!value.trim()) {
          newErrors.cardholderName = t('cardholderNameRequired');
        } else if (value.trim().length < 2) {
          newErrors.cardholderName = t('fullNameRequired');
        } else {
          delete newErrors.cardholderName;
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  const formatCardNumber = (value) => {
    const cleanValue = value.replace(/\s/g, '');
    const formatted = cleanValue.replace(/(.{4})/g, '$1 ').trim();
    return formatted.substring(0, 19); // Max 16 digits + 3 spaces
  };

  const formatExpiryDate = (value) => {
    const cleanValue = value.replace(/\D/g, '');
    if (cleanValue.length >= 2) {
      return cleanValue.substring(0, 2) + '/' + cleanValue.substring(2, 4);
    }
    return cleanValue;
  };

  const handleCardInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let formattedValue = value;
    
    if (name === 'cardNumber') {
      formattedValue = formatCardNumber(value);
    } else if (name === 'expiryDate') {
      formattedValue = formatExpiryDate(value);
    } else if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').substring(0, 4);
    }

    const newValue = type === 'checkbox' ? checked : formattedValue;

    setCardData(prev => ({
      ...prev,
      [name]: newValue
    }));

    if (type !== 'checkbox') {
      validateCardField(name, newValue);
    }
  };

  const handlePaymentMethodChange = (methodId) => {
    setPaymentMethod(methodId);
    setErrors({});
  };

  const handleAlternativePayment = async (method) => {
    setIsProcessing(true);
    
    // Simulate payment processing
    setTimeout(() => {
      setIsProcessing(false);
      console.log(`Processing ${method} payment for $${orderTotal}`);
    }, 2000);
  };

  return (
    <div className="space-y-6 text-right">
      <div className="flex items-center space-x-2">
        <Icon name="CreditCard" size={20} className="text-primary" />
        <h2 className="text-xl font-semibold text-text-primary">{t('paymentMethod')}</h2>
      </div>

      {/* Payment Method Selection */}
      <div className="space-y-3">
        {paymentMethods.map((method) => (
          <div
            key={method.id}
            className={`border rounded-[4px] p-4 cursor-pointer transition-smooth ${
              paymentMethod === method.id
                ? 'border-primary bg-primary/5' :'border-border hover:border-primary/50'
            }`}
            onClick={() => handlePaymentMethodChange(method.id)}
          >
            <div className="flex items-center space-x-3">
              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                paymentMethod === method.id
                  ? 'border-primary bg-primary' :'border-border'
              }`}>
                {paymentMethod === method.id && (
                  <div className="w-2 h-2 bg-white rounded-full"></div>
                )}
              </div>
              
              <div className="flex items-center space-x-3 flex-1">
                <Icon name={method.icon} size={20} className="text-text-secondary" />
                <div>
                  <h4 className="font-medium text-text-primary">{method.label}</h4>
                  <p className="text-sm text-text-secondary">{method.description}</p>
                </div>
              </div>

              {method.id === 'cod' && (
                <div className="text-sm text-success font-medium">
                  {t('free')}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Card Payment Form */}
      {/*paymentMethod === 'card' && (
        <div className="space-y-4 p-4 border border-border rounded-[4px] bg-muted/20">
          <div className="flex items-center space-x-2 mb-4">
            <Icon name="Lock" size={16} className="text-success" />
            <span className="text-sm text-text-secondary">{t('securePaymentInfo')}</span>
          </div>

          <Input
            label={t('cardNumber')}
            name="cardNumber"
            type="text"
            value={cardData.cardNumber}
            onChange={handleCardInputChange}
            error={errors.cardNumber}
            required
            placeholder={t('cardNumberPlaceholder')}
            maxLength={19}
          />

          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('expiryDate')}
              name="expiryDate"
              type="text"
              value={cardData.expiryDate}
              onChange={handleCardInputChange}
              error={errors.expiryDate}
              required
              placeholder={t('expiryDatePlaceholder')}
              maxLength={5}
            />
            
            <Input
              label={t('cvv')}
              name="cvv"
              type="text"
              value={cardData.cvv}
              onChange={handleCardInputChange}
              error={errors.cvv}
              required
              placeholder={t('cvvPlaceholder')}
              maxLength={4}
              description={t('cvvDescription')}
            />
          </div>

          <Input
            label={t('cardholderName')}
            name="cardholderName"
            type="text"
            value={cardData.cardholderName}
            onChange={handleCardInputChange}
            error={errors.cardholderName}
            required
            placeholder={t('cardholderNamePlaceholder')}
            description={t('nameOnCard')}
          />

          <Checkbox
            name="saveCard"
            checked={cardData.saveCard}
            onChange={handleCardInputChange}
            label={t('saveCard')}
            description={t('secureStoreCard')}
          />
        </div>
      )*/}

      {/* Cash on Delivery Info */}
      {paymentMethod === 'cod' && (
        <div className="p-4 border border-border rounded-[4px] bg-success/5">
          <div className="flex items-start space-x-3">
            <Icon name="Banknote" size={20} className="text-success mt-0.5" />
            <div>
              <h4 className="font-medium text-text-primary mb-2">{t('cashOnDelivery')}</h4>
              <ul className="text-sm text-text-secondary space-y-1">
                <li>• {t('payWhenReceive')}</li>
                <li>• {t('exactChange')}</li>
                
              </ul>
            </div>
          </div>
        </div>
      )}

 
    </div>
  );
};

export default PaymentForm;
